import {
  ALL_MENU_OBJECT_DATA,
  getMenuByProperty,
  getPageByKey,
  siteData,
} from 'utils/menu/routePageUtil';

import { getFeatureGuidePath } from 'constants/guidePaths';

/**
 * API 항목 목록을 파싱하여 메뉴 객체 배열로 변환합니다.
 * @param {Array} apiItems - API 항목 배열
 * @returns {Array} 파싱된 메뉴 아이템 배열
 */
export const parseApiItems = (apiItems) => {
  if (!apiItems || !Array.isArray(apiItems)) return [];

  return apiItems.map((apiItem) => {
    // pageKey 처리
    const pageKey = apiItem.pageKey || apiItem.key;
    const apiPageInfo = pageKey ? getPageByKey(pageKey) : {};
    const description =
      apiItem.description ||
      apiPageInfo?.description ||
      apiItem.descriptionFallback;
    let path = apiItem.path || apiPageInfo?.path || apiItem.pathFallback;
    const createdAt = apiItem.createdAt || apiPageInfo?.createdAt;
    const updatedAt = apiItem.updatedAt || apiPageInfo?.updatedAt;
    const tagType = apiItem.tagType || apiPageInfo?.tagType;
    const isNewTab = apiItem.isNewTab || apiPageInfo?.isNewTab;
    const isAbsolute = apiItem.isAbsolute || apiPageInfo?.isAbsolute;
    const title = apiItem.title || apiPageInfo?.title || apiItem.titleFallback;

    // 절대 경로 처리
    if (isAbsolute && apiItem.path) {
      path = apiItem.path;
    }

    const itemGuidePath = getFeatureGuidePath(title);
    if (itemGuidePath) {
      path = itemGuidePath;
    }

    // dataProp이 있으면 getMenuByProperty로 컨텐츠 가져오기
    if (apiItem.dataProp) {
      const { source, key } = apiItem.dataProp;
      // ALL_MENU_OBJECT_DATA[source]가 존재한다고 가정
      const sourceData = ALL_MENU_OBJECT_DATA[source];
      return {
        ...apiItem,
        title,
        description,
        path,
        content: sourceData ? getMenuByProperty(sourceData, key) : [],
      };
    }

    // content 배열 처리 (예: WEB JS 하위 메뉴)
    let content = apiItem.content;
    if (content && Array.isArray(content)) {
      content = content.map((subItem) => {
        const subPageInfo = subItem.pageKey
          ? getPageByKey(subItem.pageKey)
          : {};
        let subPath = subItem.path || subPageInfo?.path || subItem.pathFallback;

        // 절대 경로 처리 (docs 등)
        if (subItem.isAbsolute && subItem.path) {
          subPath = subItem.path;
        }

        const subTitle =
          subItem.title || subPageInfo?.title || subItem.titleFallback;
        const subGuidePath = getFeatureGuidePath(subTitle);
        if (subGuidePath) {
          subPath = subGuidePath;
        }

        return {
          ...subItem,
          title: subTitle,
          path: subPath,
          isNewTab: subItem.isNewTab || subPageInfo?.isNewTab,
        };
      });
    }

    return {
      ...apiItem,
      title,
      description,
      path,
      createdAt,
      updatedAt,
      tagType,
      isNewTab,
      isAbsolute,
      content,
    };
  });
};

/**
 * site.json의 섹션(API 및 SDK 내부의 지도, 검색, 경로 등)을 파싱하여 메뉴 객체로 변환합니다.
 * @param {Object} section - site.json의 섹션 객체
 * @returns {Array} 파싱된 메뉴 아이템 배열
 */
export const parseSectionApis = (section) => {
  if (!section) return [];

  // 1. Array 형태의 apis가 바로 있는 경우
  if (section.apis) {
    return parseApiItems(section.apis);
  }

  // 2. verticals가 존재하는 경우
  if (section.verticals) {
    return parseApiItems(section.verticals);
  }

  // 3. dataProp으로 외부 JSON을 참조하는 경우
  if (section.dataProp) {
    const { source, key } = section.dataProp;
    const sourceData = ALL_MENU_OBJECT_DATA[source];
    // sourceData[key] 가 API items list 라고 가정
    const apiItems = sourceData && sourceData[key] ? sourceData[key] : [];
    return parseApiItems(apiItems);
  }

  return [];
};

/**
 * Service 메뉴 그룹을 파싱하여 GNB(Global Navigation Bar)에 사용할 구조로 변환합니다.
 * @param {Object} serviceGroups - site.json의 service_groups 객체
 * @returns {Array} 파싱된 서비스 그룹 배열
 */
export const getServiceGroups = (serviceGroups) => {
  // siteData.menu.서비스 배열 (예: ["service_recommendation", ...])을 순회하여
  // service_groups의 내용을 매핑합니다.
  const serviceKeys = siteData.menu?.['서비스'] || [];

  return serviceKeys
    .map((key) => {
      const groupData = serviceGroups[key];
      if (!groupData) return null;

      return {
        ...groupData,
        sections: groupData.sections.map((section) => {
          // Recommendation 처리
          if (section.recommendation) {
            const recPageInfo = section.recommendation.pageKey
              ? getPageByKey(section.recommendation.pageKey)
              : {};
            section.recommendation = {
              ...section.recommendation,
              path: recPageInfo?.path || section.recommendation.path,
            };

            // PageKey로 기본 field 채우기
            const pageInfo = section.pageKey
              ? getPageByKey(section.pageKey)
              : {};
            return {
              ...section,
              path: pageInfo?.path || section.path,
            };
          }

          // APIs 처리 (지도/검색/경로 등)
          if (section.apis || section.verticals || section.dataProp) {
            // pageKey로 path/desc 채우기
            const pageInfo = section.pageKey
              ? getPageByKey(section.pageKey)
              : {};
            const parsedApis = parseSectionApis(section);
            const result = {
              ...section,
              path: pageInfo?.path || section.path,
            };
            if (section.apis || section.dataProp) result.apis = parsedApis;
            if (section.verticals) result.verticals = parsedApis;
            return result;
          }

          return section;
        }),
      };
    })
    .filter(Boolean);
};

/**
 * Console 메뉴(대시보드) 데이터를 site.json 기반으로 생성합니다.
 * @returns {Array} 콘솔 메뉴 아이템 배열
 */
export const getConsoleMenu = () => {
  const consoleKeys = siteData.menu?.['CONSOLE'] || [];

  return consoleKeys.map((key) => {
    // 특수한 키값 처리 (결제/상품)
    if (key === 'console_billing_management') {
      return {
        title: '결제/상품',
        content: getMenuByProperty(
          ALL_MENU_OBJECT_DATA.billing,
          'console_billing_management'
        ).map((item) => {
          if (item.path.includes('card-management')) {
            return {
              ...item,
              path: item.path?.replace(/\/\*$/, ''),
            };
          }
          return item;
        }),
      };
    }

    // 일반 페이지 키 처리
    const pageInfo = getPageByKey(key);
    return {
      title: pageInfo?.title || key,
      path: pageInfo?.path || '',
    };
  });
};
